import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import components (lazy-loaded for performance)
const PCIRegister = React.lazy(() => import("./PCIRegister"));
const NewPCIEntry = React.lazy(() => import("./NewPCIEntry"));
const PCIEntryDetails = React.lazy(() => import("./PCIEntryDetails"));
const ReportIssue = React.lazy(() => import("./ReportIssue"));
const IssuesList = React.lazy(() => import("./IssuesList"));
const IssueDetails = React.lazy(() => import("./IssueDetails"));
const GovernanceMeetingsList = React.lazy(() => import("./GovernanceMeetingsList"));
const NewGovernanceMeeting = React.lazy(() => import("./NewGovernanceMeeting"));
const GovernanceMeetingDetails = React.lazy(() => import("./GovernanceMeetingDetails"));
const ActivityDetails = React.lazy(() => import("./ActivityDetails"));

/**
 * CONTINUOUS IMPROVEMENT MODULE
 *
 * Compliant with Section 62 of the Aged Care Quality and Safety Commission Rules 2018.
 * Tracks improvement initiatives using the PCI Register format (FRM-ACC-0550 v1.1).
 *
 * Features:
 * - PCI Register matching official ACQSC template format
 * - 8 Quality Standards tracking
 * - Issue reporting (available to all staff)
 * - Governance meeting documentation
 * - PDF export for regulators
 *
 * For more information about continuous improvement requirements:
 * https://www.agedcarequality.gov.au/continuous-improvement
 */
const continuousImprovementModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "continuous-improvement",
    name: "Continuous Improvement",
    description: "Quality improvement tracking compliant with ACQSC Section 62 requirements",
    version: "1.0.0",
    enabled: true,
    category: "quality",
    order: 6,
    icon: "📋",
    color: "#1E40AF",

    // ROUTES
    routes: [
        // PCI Register (Main view)
        {
            path: "/continuous-improvement",
            component: PCIRegister,
            permissions: ["USER"]
        },
        {
            path: "/continuous-improvement/new",
            component: NewPCIEntry,
            permissions: ["CI_MANAGER", "CI_ADMIN"]
        },
        {
            path: "/continuous-improvement/initiative/:id",
            component: PCIEntryDetails,
            permissions: ["USER"]
        },
        // Issue Reporting
        {
            path: "/continuous-improvement/report-issue",
            component: ReportIssue,
            permissions: ["USER"]
        },
        {
            path: "/continuous-improvement/issues",
            component: IssuesList,
            permissions: ["USER"]
        },
        {
            path: "/continuous-improvement/issues/:id",
            component: IssueDetails,
            permissions: ["USER"]
        },
        // Governance Meetings
        {
            path: "/continuous-improvement/meetings",
            component: GovernanceMeetingsList,
            permissions: ["USER"]
        },
        {
            path: "/continuous-improvement/meetings/new",
            component: NewGovernanceMeeting,
            permissions: ["CI_ADMIN"]
        },
        {
            path: "/continuous-improvement/meetings/:id",
            component: GovernanceMeetingDetails,
            permissions: ["USER"]
        },
        // Activity Details
        {
            path: "/continuous-improvement/activity/:sourceType/:sourceId/:activityIndex",
            component: ActivityDetails,
            permissions: ["USER"]
        }
    ],

    // NAVIGATION
    navigation: [
        {
            label: "PCI Register",
            path: "/continuous-improvement",
            icon: "📋",
            permissions: ["USER"]
        },
        {
            label: "Report Issue",
            path: "/continuous-improvement/report-issue",
            icon: "🚨",
            permissions: ["USER"]
        },
        {
            label: "Issues",
            path: "/continuous-improvement/issues",
            icon: "📝",
            permissions: ["USER"]
        },
        {
            label: "Governance Meetings",
            path: "/continuous-improvement/meetings",
            icon: "👥",
            permissions: ["USER"]
        }
    ],

    // PERMISSIONS
    permissions: {
        view: ["USER"],
        create: ["CI_MANAGER", "CI_ADMIN"],
        edit: ["CI_MANAGER", "CI_ADMIN"],
        delete: ["CI_ADMIN"]
    },

    // QUICK ACTIONS
    quickActions: [
        {
            label: "Report Issue",
            path: "/continuous-improvement/report-issue",
            icon: "🚨",
            description: "Report a quality issue or concern"
        },
        {
            label: "View PCI Register",
            path: "/continuous-improvement",
            icon: "📋",
            description: "View the Plan for Continuous Improvement register"
        },
        {
            label: "New PCI Entry",
            path: "/continuous-improvement/new",
            icon: "➕",
            description: "Create a new improvement initiative"
        }
    ],

    // CHANGELOG
    changelog: [
        {
            version: "1.0.0",
            releaseDate: "2025-12-23",
            title: "Initial Release - Continuous Improvement Module",
            features: [
                "PCI Register matching FRM-ACC-0550 template format",
                "8 Quality Standards tracking with sub-clauses",
                "Issue reporting for all staff with photo attachments",
                "Governance meeting documentation with minutes",
                "Status workflow (Identified → Planned → In Progress → Under Review → Completed)",
                "CI Manager and CI Admin roles with appropriate permissions",
                "Regulator read-only view with PDF export",
                "Weekly summary email reports (Tuesdays)"
            ],
            notes: "Initial release of the Continuous Improvement module compliant with Section 62 of the Aged Care Quality and Safety Commission Rules 2018. Provides regulator-ready documentation for audits."
        }
    ],

    // ROADMAP
    roadmap: [
        {
            title: "PDF Export",
            description: "Export PCI Register to PDF in official FRM-ACC-0550 format",
            targetVersion: "1.1.0",
            priority: "high",
            status: "planned",
            features: [
                "Export to official ACQSC template format",
                "Include all 8 columns from FRM-ACC-0550",
                "Filter by date range and quality standard"
            ]
        },
        {
            title: "Dashboard Analytics",
            description: "Visual dashboard for quality improvement metrics",
            targetVersion: "1.2.0",
            priority: "medium",
            status: "planned",
            features: [
                "Quality Standards coverage overview",
                "Overdue reviews and completions alerts",
                "Trends and completion rate metrics"
            ]
        }
    ]
};

export default continuousImprovementModuleConfig;
